/**
 * Skeletons temáticos — a casca carrega inteira e as ÁREAS DE DADOS pulsam
 * no mesmo layout final (zero shift). O empty state só aparece depois que a
 * primeira carga confirmar que não há dados de verdade.
 */
import type { CSSProperties } from 'react';
import { cn } from '../../lib/utils';

/** Bloco de skeleton na paleta da noite. */
export function Sk({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <span
      className={cn('block animate-pulse rounded-[8px] bg-foreground/[0.07]', className)}
      style={style}
      aria-hidden
    />
  );
}

/** Início ("O céu da sua escrita") — espelha o grid real. */
export function InicioSkeleton() {
  return (
    <div className="flex-1 min-h-0 overflow-hidden">
      <div className="px-4 py-5 md:px-[30px] md:py-5 w-full md:h-full flex flex-col gap-4 md:gap-3.5">
        <div className="shrink-0 space-y-2">
          <Sk className="h-2.5 w-36" />
          <Sk className="h-7 w-64" />
        </div>
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4 md:gap-3.5 shrink-0">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="dcard min-w-0 space-y-3">
              <Sk className="h-2 w-24" />
              <Sk className="h-8 w-28" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:flex-[1.1] md:min-h-0 shrink-0 md:shrink">
          {[0, 1].map((i) => (
            <div key={i} className="dcard flex flex-col h-[230px] md:h-auto space-y-3">
              <Sk className="h-2 w-40" />
              <Sk className="min-h-0 w-full flex-1 !rounded-[12px]" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 md:flex-1 md:min-h-0 shrink-0 md:shrink">
          {[0, 1, 2].map((i) => (
            <div key={i} className="dcard flex flex-col space-y-3">
              <Sk className="h-2 w-32" />
              <Sk className="h-9 w-full" />
              <Sk className="h-9 w-full" />
              <Sk className="h-9 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Biblioteca/Estante — livro aberto + prateleira pulsando. */
export function EstanteSkeleton() {
  const spines = [150, 190, 160, 180, 145, 185, 155];
  return (
    <div className="flex-1 min-h-0 flex flex-col justify-evenly gap-4 md:gap-6 py-2">
      {/* livro aberto */}
      <div className="mx-auto w-full max-w-[760px] grid grid-cols-2 gap-[2px]">
        <Sk className="h-[clamp(216px,36dvh,306px)] !rounded-[10px_2px_2px_10px] !bg-foreground/[0.09]" />
        <Sk className="h-[clamp(216px,36dvh,306px)] !rounded-[2px_10px_10px_2px] !bg-foreground/[0.09]" />
      </div>
      {/* prateleira */}
      <div>
        <div className="flex items-end gap-[7px] px-2 pt-6">
          {spines.map((h, i) => (
            <Sk
              key={i}
              className="!rounded-[5px_5px_2px_2px]"
              style={{ height: h, width: 30 + (i % 3) * 5 }}
            />
          ))}
        </div>
        <Sk className="mt-1 h-[10px] w-full !rounded-[4px]" />
      </div>
    </div>
  );
}
